/**
 * PurchaseReturnsScreen — goods returned to suppliers over a period, rolled up
 * by supplier and by reason. The purchase-side returns record and a quality
 * signal on what is going back.
 *
 * Computed entirely from local WatermelonDB — no network required.
 */

import { useCallback, useEffect, useState } from 'react';
import { Download, RefreshCw } from 'lucide-react';
import { useAppStore } from '@/store/useAppStore';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format';
import {
  presetRange,
  toDateInputValue,
  startOfDay,
  lastIncludedInstant,
  type RangePreset as Preset,
} from '@/utils/dateRange';
import { Button } from '@/components/ui/Button';
import { pdf, file } from '@/platform';
import { toCsv } from '@/utils/csv';
import { loadPurchaseReturns } from '@/reports/purchaseReturns/purchaseReturnsDataSource';
import { buildPurchaseReturns, type PurchaseReturns } from '@/reports/purchaseReturns/purchaseReturns';

// ─── Component ───────────────────────────────────────────────────────────────

export function PurchaseReturnsScreen() {
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const [preset, setPreset] = useState<Preset>('month');
  const [customFrom, setCustomFrom] = useState(toDateInputValue(startOfDay(new Date())));
  const [customTo, setCustomTo]     = useState(toDateInputValue(new Date()));
  const [data, setData]             = useState<PurchaseReturns | null>(null);
  const [loading, setLoading]       = useState(true);
  const [exporting, setExporting]   = useState(false);

  const range = useCallback(
    (): [Date, Date] => presetRange(preset, { from: customFrom, to: customTo }),
    [preset, customFrom, customTo],
  );

  const load = useCallback(async () => {
    if (!activeStoreId) return;
    setLoading(true);
    try {
      const [from, to] = range();
      setData(buildPurchaseReturns(await loadPurchaseReturns(activeStoreId, from, to)));
    } finally {
      setLoading(false);
    }
  }, [activeStoreId, range]);

  useEffect(() => { void load(); }, [load]);

  const rangeStamp = (): string => {
    const [from, to] = range();
    return `${toDateInputValue(from)}_${toDateInputValue(lastIncludedInstant(to))}`;
  };

  const handleExportCsv = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const csv = toCsv(data.rows, [
        { header: 'Date', value: (r) => toDateInputValue(r.date) },
        { header: 'Return', value: (r) => r.returnNumber },
        { header: 'Supplier', value: (r) => r.supplierName },
        { header: 'Reason', value: (r) => r.reasonLabel },
        { header: 'Status', value: (r) => r.status },
        { header: 'Qty', value: (r) => r.quantity },
        { header: 'Value', value: (r) => r.value },
      ]);
      await file.saveText({
        content: csv,
        defaultFileName: `PurchaseReturns_${rangeStamp()}.csv`,
        filters: [{ name: 'CSV', extensions: ['csv'] }],
      });
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const [from, to] = range();
      await pdf.saveFromHtml({
        html: buildReturnsHtml(data, from, to),
        defaultFileName: `PurchaseReturns_${rangeStamp()}.pdf`,
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex h-full flex-col gap-4 overflow-auto pb-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        {(['today', 'week', 'month', 'custom'] as Preset[]).map((p) => (
          <button key={p} type="button" onClick={() => setPreset(p)}
            className={cn('rounded-md px-3 py-1.5 text-body transition-colors',
              preset === p ? 'bg-accent/10 text-accent-ink' : 'text-fg-secondary hover:bg-bg-subtle')}>
            {p === 'today' ? 'Today' : p === 'week' ? 'Last 7 days' : p === 'month' ? 'This month' : 'Custom'}
          </button>
        ))}
        {preset === 'custom' && (
          <div className="flex items-center gap-2">
            <input type="date" value={customFrom} max={customTo}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1 text-body text-fg-primary" />
            <span className="text-fg-tertiary">–</span>
            <input type="date" value={customTo} min={customFrom}
              onChange={(e) => setCustomTo(e.target.value)}
              className="rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1 text-body text-fg-primary" />
          </div>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button variant="secondary" onClick={() => void load()} disabled={loading}>
            <RefreshCw size={14} className={cn(loading && 'animate-spin')} />
          </Button>
          <Button variant="secondary" onClick={() => void handleExportCsv()} disabled={!data || exporting || data.rows.length === 0}>
            <Download size={14} className="mr-1.5" /> CSV
          </Button>
          <Button variant="secondary" onClick={() => void handleExportPdf()} disabled={!data || exporting || data.rows.length === 0}>
            <Download size={14} className="mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center text-body text-fg-tertiary">Loading…</div>
      ) : !data ? null : data.rows.length === 0 ? (
        <div className="mt-16 text-center text-body text-fg-tertiary">No purchase returns in this period.</div>
      ) : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <KPI label="Returns" value={String(data.totals.count)} />
            <KPI label="Units returned" value={String(data.totals.quantity)} />
            <KPI label="Return value" value={formatCurrency(data.totals.value)} accent />
          </div>

          {/* Reason breakdown */}
          {data.byReason.length > 0 && (
            <Card title="By reason">
              <div className="flex flex-wrap gap-2">
                {data.byReason.map((r) => (
                  <span key={r.reason} className="rounded-md bg-bg-subtle px-2 py-1 text-label text-fg-secondary">
                    {r.reason}: {formatCurrency(r.value)} ({r.count})
                  </span>
                ))}
              </div>
            </Card>
          )}

          {/* Returns table */}
          <Card title="Returns">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[680px] text-body">
                <thead>
                  <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
                    <th className="pb-1 text-left font-normal">Date</th>
                    <th className="pb-1 text-left font-normal">Return</th>
                    <th className="pb-1 text-left font-normal">Supplier</th>
                    <th className="pb-1 text-left font-normal">Reason</th>
                    <th className="pb-1 text-right font-normal">Qty</th>
                    <th className="pb-1 text-right font-normal">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r, i) => (
                    <tr key={i} className="border-b border-stroke-hairline/10">
                      <td className="py-1.5 text-fg-secondary tabular-nums">{r.date.toLocaleDateString('en-IN')}</td>
                      <td className="py-1.5 text-fg-secondary tabular-nums">{r.returnNumber}</td>
                      <td className="py-1.5 text-fg-primary">{r.supplierName}</td>
                      <td className="py-1.5 text-fg-tertiary">{r.reasonLabel}</td>
                      <td className="py-1.5 text-right tabular-nums text-fg-secondary">{r.quantity}</td>
                      <td className="py-1.5 text-right font-medium tabular-nums text-fg-primary">{formatCurrency(r.value)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-stroke-hairline/20 font-medium">
                    <td className="pt-2 text-fg-primary" colSpan={4}>Total</td>
                    <td className="pt-2 text-right tabular-nums text-fg-primary">{data.totals.quantity}</td>
                    <td className="pt-2 text-right tabular-nums text-accent-ink">{formatCurrency(data.totals.value)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

// ─── Small helpers ────────────────────────────────────────────────────────────

function KPI({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="text-label uppercase text-fg-tertiary">{label}</div>
      <div className={cn('mt-1 text-heading tabular-nums', accent ? 'text-accent-ink' : 'text-fg-primary')}>{value}</div>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="mb-2 text-label uppercase text-fg-tertiary">{title}</div>
      {children}
    </div>
  );
}

// ─── PDF export ───────────────────────────────────────────────────────────────

function buildReturnsHtml(d: PurchaseReturns, from: Date, toExclusive: Date): string {
  const fmt = (n: number) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n);
  const fmtDate = (dt: Date) => dt.toLocaleDateString('en-IN');
  const to = lastIncludedInstant(toExclusive);
  const esc = (s: string) => s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c] as string);

  const rows = d.rows.map((r) =>
    `<tr><td>${fmtDate(r.date)}</td><td>${esc(r.returnNumber)}</td><td>${esc(r.supplierName)}</td>
     <td>${esc(r.reasonLabel)}</td><td style="text-align:right">${r.quantity}</td>
     <td style="text-align:right">${fmt(r.value)}</td></tr>`,
  ).join('');

  return `<!DOCTYPE html><html><head><meta charset="utf-8">
<style>body{font-family:sans-serif;margin:32px;color:#222}h1{font-size:22px;margin-bottom:2px}
table{width:100%;border-collapse:collapse;font-size:11px;margin-top:10px}th,td{padding:5px 7px;border-bottom:1px solid #eee}
th{text-align:right;color:#666;font-weight:500}th:nth-child(-n+4),td:nth-child(-n+4){text-align:left}
tfoot td{border-top:2px solid #333;font-weight:700}</style></head>
<body>
<h1>Purchase Returns</h1>
<p style="color:#888">${fmtDate(from)} – ${fmtDate(to)} · ${d.totals.count} returns · ${d.totals.quantity} units · ${fmt(d.totals.value)}</p>
<table>
<thead><tr><th>Date</th><th>Return</th><th>Supplier</th><th>Reason</th><th style="text-align:right">Qty</th><th style="text-align:right">Value</th></tr></thead>
<tbody>${rows}</tbody>
<tfoot><tr><td colspan="4">Total</td><td style="text-align:right">${d.totals.quantity}</td><td style="text-align:right">${fmt(d.totals.value)}</td></tr></tfoot>
</table>
<p style="margin-top:36px;font-size:11px;color:#aaa">Generated by BigxBook Desktop on ${new Date().toLocaleString('en-IN')}</p>
</body></html>`;
}
